import get from 'lodash/get';
import unionBy from 'lodash/unionBy';
import {SESSION_SOURCE} from '@techsee/techsee-common/lib/constants/room.constants';
import {IInteractionSummaryService} from '@techsee/techsee-client-infra/lib/services/InteractionSummaryService';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {IEventLogService} from './event-log/event-log.service';

export class InteractionSummaryHelperService {
    private interactionSummaryService: IInteractionSummaryService;
    private accountData: any;
    private roomData: any;
    private translate: (key: string) => string;
    private eventLogService: IEventLogService;
    constructor(
        interactionSummaryService: IInteractionSummaryService,
        accountData: any,
        roomData: any,
        translate: (key: string) => string,
        eventLogService: IEventLogService
    ) {
        this.interactionSummaryService = interactionSummaryService;
        this.accountData = accountData;
        this.roomData = roomData;
        this.translate = translate;
        this.eventLogService = eventLogService;
    }

    async getSummaryData(callParams?: any, page?: number, perPage?: number): Promise<any> {
        const finalPerPage = get(this.accountData, 'protectedSettings.interactionSummary.perPage', perPage);
        const roomId = callParams?.sessionId || get(this.roomData, '_id', undefined);

        if (!roomId) {
            throw new Error('no session id was provided');
        }

        const interactionSummaryResults = await (callParams?.sessionId
            ? this.interactionSummaryService.getInteractionSummary(roomId, page, finalPerPage)
            : this.interactionSummaryService.getWarmTransferInteractionSummary(roomId, page, finalPerPage));

        if (get(interactionSummaryResults, 'sessionSource')) {
            interactionSummaryResults.sessionSource =
                callParams?.flowName || this.translate('REACT.INTERACTION.SUMMARY.INTERACTION.SUMMARY');
        }

        if (callParams?.creationTimestamp) {
            interactionSummaryResults.creationTimestamp = callParams.creationTimestamp;
        }

        return interactionSummaryResults;
    }

    async getSummaryPreviews(
        customerId: string | undefined,
        enableAIInteractionSummary: boolean,
        enableVJInteractionSummary: boolean,
        formatSessionSource?: (source: string) => string
    ): Promise<any[]> {
        try {
            let vjRes;
            let aiRes;
            const pageSize = get(this.accountData, 'protectedSettings.interactionSummary.historySearchPerPage');

            if (enableVJInteractionSummary) {
                try {
                    vjRes = await this.interactionSummaryService.listInteractionSummaries(
                        this.accountData._id,
                        customerId,
                        undefined,
                        undefined,
                        pageSize,
                        undefined,
                        SESSION_SOURCE.VJ
                    );
                } catch (e) {
                    this.eventLogService.getInteractionSummaryPreviewsVJFailed({
                        error: e
                    });
                }
            }

            if (enableAIInteractionSummary) {
                try {
                    aiRes = await this.interactionSummaryService.listInteractionSummaries(
                        this.accountData._id,
                        customerId,
                        undefined,
                        undefined,
                        pageSize,
                        undefined,
                        SESSION_SOURCE.AI
                    );
                } catch (e) {
                    this.eventLogService.getInteractionSummaryPreviewsAIFailed({
                        error: e
                    });
                }
            }

            const res = unionBy(vjRes?.previews || [], aiRes?.previews || [], 'sessionId');

            if (res && res.length) {
                return res.map((preview: any) => {
                    preview.itemsText =
                        preview.sessionSource === SESSION_SOURCE.VJ
                            ? this.translate('REACT.INTERACTION.SUMMARY.VJ.LIST.IMAGES')
                            : this.translate('REACT.INTERACTION.SUMMARY.STEPS');

                    preview.totalResults =
                        preview.sessionSource === SESSION_SOURCE.VJ
                            ? preview.visualJourneyTotalImagesUploaded
                            : preview.totalResults;

                    if (formatSessionSource) {
                        preview.sessionSource = formatSessionSource(preview.sessionSource);
                    } else if (
                        preview.sessionSource === SESSION_SOURCE.AI ||
                        preview.sessionSource === SESSION_SOURCE.VJ
                    ) {
                        preview.sessionSource = this.translate('REACT.INTERACTION.SUMMARY.INTERACTION.SUMMARY');
                    }

                    return preview;
                });
            }

            return [];
        } catch (err) {
            this.eventLogService.getInteractionSummaryPreviewsFailed({
                error: err
            });
            return [];
        }
    }
}
